const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const TOKEN_KEY = 'abga_dashboard_token';

const REFRESH_KEY = 'abga_dashboard_refresh_token';



export const getToken = () => localStorage.getItem(TOKEN_KEY);

export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);



export const setAuthTokens = (accessToken, refreshToken) => {

  localStorage.setItem(TOKEN_KEY, accessToken);

  localStorage.setItem(REFRESH_KEY, refreshToken);

};



/** @deprecated use setAuthTokens */

export const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);



export const clearAuthTokens = () => {

  localStorage.removeItem(TOKEN_KEY);

  localStorage.removeItem(REFRESH_KEY);

};



/** @deprecated use clearAuthTokens */

export const clearToken = () => clearAuthTokens();



export const hasAuthSession = () => !!(getToken() || getRefreshToken());



export class ApiError extends Error {

  constructor(message, status) {

    super(message);

    this.status = status;

  }

}



let refreshPromise = null;



async function parseResponse(res) {

  if (res.status === 204) return null;



  let body = null;

  const text = await res.text();

  if (text) {

    try { body = JSON.parse(text); } catch { body = text; }

  }

  return body;

}



function errorMessage(body, status) {

  const msg =

    (body && (body.message || body.error)) ||

    (typeof body === 'string' ? body : null) ||

    `Request failed (${status})`;

  return Array.isArray(msg) ? msg.join(', ') : msg;

}



async function refreshAccessToken() {

  const refreshToken = getRefreshToken();

  if (!refreshToken) {

    throw new ApiError('Session expired', 401);

  }



  let res;

  try {

    res = await fetch(`${API_URL}/auth/refresh`, {

      method: 'POST',

      headers: { 'Content-Type': 'application/json' },

      body: JSON.stringify({ refreshToken }),

    });

  } catch {

    throw new ApiError('Network error. Is the API running?', 0);

  }



  const body = await parseResponse(res);

  if (!res.ok) {

    clearAuthTokens();

    throw new ApiError(errorMessage(body, res.status), res.status);

  }



  setAuthTokens(body.accessToken, body.refreshToken);

  return body;

}



async function apiFetch(path, options = {}, retried = false) {

  const token = getToken();

  const headers = {

    'Content-Type': 'application/json',

    ...(options.headers || {}),

  };

  if (token) headers.Authorization = `Bearer ${token}`;



  let res;

  try {

    res = await fetch(`${API_URL}${path}`, { ...options, headers });

  } catch {

    throw new ApiError('Network error. Is the API running?', 0);

  }



  const isAuthRoute = path.startsWith('/auth/login') || path.startsWith('/auth/refresh');



  if (res.status === 401 && !retried && !isAuthRoute && getRefreshToken()) {

    try {

      if (!refreshPromise) {

        refreshPromise = refreshAccessToken().finally(() => { refreshPromise = null; });

      }

      await refreshPromise;

      return apiFetch(path, options, true);

    } catch (e) {

      clearAuthTokens();

      throw e;

    }

  }



  const body = await parseResponse(res);



  if (!res.ok) {

    if (res.status === 401) clearAuthTokens();

    throw new ApiError(errorMessage(body, res.status), res.status);

  }



  return body;

}



/* ── auth ─────────────────────────────────────────────── */

export const apiLogin = (email, password) =>

  apiFetch('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });



export const apiRefresh = () => refreshAccessToken();



export const apiLogout = (refreshToken) =>

  apiFetch('/auth/logout', {

    method: 'POST',

    body: JSON.stringify({ refreshToken: refreshToken || undefined }),

  });



export const apiGetMe = () => apiFetch('/auth/me');



/* ── projects ─────────────────────────────────────────── */

export const apiGetProjects = () => apiFetch('/projects');



export const apiCreateProject = (data) =>

  apiFetch('/projects', { method: 'POST', body: JSON.stringify(data) });



export const apiUpdateProject = (id, changes) =>

  apiFetch(`/projects/${id}`, { method: 'PATCH', body: JSON.stringify(changes) });



export const apiDeleteProject = (id) =>

  apiFetch(`/projects/${id}`, { method: 'DELETE' });



/* ── tasks ────────────────────────────────────────────── */

export const apiCreateTask = (projectId, data) =>

  apiFetch(`/projects/${projectId}/tasks`, { method: 'POST', body: JSON.stringify(data) });



export const apiUpdateTask = (projectId, taskId, changes) =>

  apiFetch(`/projects/${projectId}/tasks/${taskId}`, { method: 'PATCH', body: JSON.stringify(changes) });



export const apiDeleteTask = (projectId, taskId) =>

  apiFetch(`/projects/${projectId}/tasks/${taskId}`, { method: 'DELETE' });



/* ── notes ────────────────────────────────────────────── */

export const apiCreateNote = (projectId, text) =>

  apiFetch(`/projects/${projectId}/notes`, { method: 'POST', body: JSON.stringify({ text }) });



export const apiDeleteNote = (projectId, noteId) =>

  apiFetch(`/projects/${projectId}/notes/${noteId}`, { method: 'DELETE' });



/* ── servers (VPS monitor proxy) ─────────────────────── */

export const apiGetServers = () => apiFetch('/servers');

export const apiGetServerMetrics = (id) => apiFetch(`/servers/${id}/metrics`);

export const apiGetServerHealth = (id) => apiFetch(`/servers/${id}/health`);

/* ── cybersecurity ───────────────────────────────────── */

export const apiGetCybersecuritySummary = () => apiFetch('/cybersecurity/summary');

export const apiGetCybersecurityIpCounters = () => apiFetch('/cybersecurity/ip-counters');

export const apiGetCybersecurityEvents = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value !== '') qs.set(key, String(value));
  });
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch(`/cybersecurity/events${suffix}`);
};

export const apiGetCybersecurityMonitored = (status) => {
  const suffix = status ? `?status=${encodeURIComponent(status)}` : '';
  return apiFetch(`/cybersecurity/monitored${suffix}`);
};

export const apiCreateCybersecurityMonitored = (source, data) =>
  apiFetch(`/cybersecurity/monitored/${encodeURIComponent(source)}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const apiRevertCybersecurityMonitored = (source, id) =>
  apiFetch(`/cybersecurity/monitored/${encodeURIComponent(source)}/${id}/revert`, {
    method: 'POST',
  });

export const apiGetCybersecurityIpDetail = (source, ip) =>
  apiFetch(`/cybersecurity/ip/${encodeURIComponent(source)}/${encodeURIComponent(ip)}`);

export const apiGetCybersecurityEventDetail = (source, id) =>
  apiFetch(`/cybersecurity/events/${encodeURIComponent(source)}/${id}`);

export const apiGetCybersecurityMonitoredDetail = (source, id) =>
  apiFetch(`/cybersecurity/monitored/${encodeURIComponent(source)}/${id}`);

export const apiGetRegistryVehicleUpdates = (date) => {
  const suffix = date ? `?date=${encodeURIComponent(date)}` : '';
  return apiFetch(`/cybersecurity/registry-vehicle-updates${suffix}`);
};

export const apiGetRegistryVehicleUpdate = (id) =>
  apiFetch(`/cybersecurity/registry-vehicle-updates/${encodeURIComponent(id)}`);

